import React, { useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Button,
  Typography,
  Box,
  Stack,
  IconButton,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Close, Lock, TrendingUp, CalendarMonth } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const NON_DTAA_MESSAGES = {
  quota_exhausted: 'You have used all 10 free link clicks for this month.',
  no_credits: 'You have no remaining quota to access this content.',
  link_filtered:
    'Links, GTL Notes and Related Documents have been removed from this document due to quota limits.',
  default: 'Upgrade your account to access this content.',
}

const DTAA_MESSAGES = {
  quota_exhausted: 'You have used all 1 free DTAA views for this month.',
  no_credits: 'You have no remaining quota to access this content.',
  default: 'Upgrade your account to access this content.',
}

const formatQuotaResetDate = (input) => {
  if (!input) return null

  try {
    let date = null

    if (Array.isArray(input)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = input
      if (!year || !month || !day) return null
      date = new Date(year, month - 1, day, hour, minute, second)
    } else if (input instanceof Date) {
      date = input
    } else if (typeof input === 'string') {
      date = new Date(input)
    }

    if (!date || Number.isNaN(date.getTime())) return null

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch (error) {
    console.error('Error formatting quota reset date:', error)
    return null
  }
}

const UpgradePromptModal = ({
  open,
  onClose,
  reason = 'quota_exhausted',
  quotaResetDate = null,
  documentType = 'link', // 'link' or 'dtaa'
  forceDialog = false,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isDtaa = documentType === 'dtaa'
  const isCloseable = typeof onClose === 'function'
  const handleClose = isCloseable ? onClose : () => {}
  const formattedQuotaResetDate = useMemo(
    () => formatQuotaResetDate(quotaResetDate),
    [quotaResetDate],
  )

  const message = useMemo(() => {
    const messageMap = isDtaa ? DTAA_MESSAGES : NON_DTAA_MESSAGES
    return messageMap[reason] || messageMap.default
  }, [isDtaa, reason])

  const title = isDtaa ? 'DTAA Access Restricted' : 'Content Access Limited'

  if (!open) {
    return null
  }

  const content = (
    <>
      {(!isDtaa || isCloseable) && (
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
          }}
        >
          <Close />
        </IconButton>
      )}

      <DialogTitle sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#fff3cd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <Lock sx={{ fontSize: 40, color: '#ff9800' }} />
        </Box>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{ fontWeight: 600, color: '#232536' }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: isDtaa ? 4 : 2 }}>
        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 3,
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {message}
        </Typography>

        {formattedQuotaResetDate && (
          <Box
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              mb: 3,
              textAlign: 'center',
            }}
          >
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='center'
            >
              <CalendarMonth sx={{ color: '#666', fontSize: 20 }} />
              <Typography variant='body2' color='text.secondary'>
                Your free quota resets on{' '}
                <strong>{formattedQuotaResetDate}</strong>
              </Typography>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography
          variant='subtitle2'
          sx={{ fontWeight: 600, mb: 2, color: '#232536' }}
        >
          Choose an option to continue:
        </Typography>

        <Stack spacing={2}>
          <Box
            sx={{
              p: 2,
              border: '2px solid #232536',
              borderRadius: 2,
              bgcolor:'#ffffff',// '#f0fdf4',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(35, 37, 54, 0.2)',
              },
            }}
            onClick={() => {
              navigate('/pricing')
              handleClose()
            }}
          >
            <Stack direction='row' spacing={2} alignItems='center'>
              <TrendingUp sx={{ fontSize: 32, color: '#232536' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                  Upgrade now
                </Typography>
                {!isDtaa && (
                  <Typography variant='caption' color='text.secondary'>
                    Unlimited access to links, GTL Notes and Related Documents
                  </Typography>
                )}
              </Box>
              <Chip
                label='Recommended'
                size='small'
                sx={{ bgcolor: '#232536', color: 'white' }}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {!isDtaa && (
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
          <Button
            onClick={handleClose}
            variant='outlined'
            size={isMobile ? 'medium' : 'large'}
            sx={{
              borderRadius: 2,
              px: 3,
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              // Text and border color for the initial state
              color: '#232536',
              borderColor: '#232536',
              '&:hover': {
                bgcolor: '#ffcf51',
                color: '#232536',
                borderColor: '#ffcf51', // Matching border to the hover background
                fontWeight: '600',
              },
            }}
          >
            Continue with free tier
          </Button>
        </DialogActions>
      )}
    </>
  )

  if (isDtaa && !forceDialog) {
    return (
      <Paper
        sx={{
          borderRadius: 3,
          position: 'relative',
          maxWidth: 620,
          mx: 'auto',
          mt: 2,
        }}
      >
        {content}
      </Paper>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      transitionDuration={0}
      sx={{ zIndex: 20000 }}
      BackdropProps={{
        transitionDuration: 0,
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.58)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          position: 'relative',
          transformOrigin: '50% 45%',
          willChange: 'transform, opacity, filter',
          animation: 'upgradeModalPopIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
          '@keyframes upgradeModalPopIn': {
            from: {
              transform: 'translateY(10px) scale(0.965)',
              opacity: 0.96,
              filter: 'saturate(0.96)',
            },
            to: {
              transform: 'translateY(0) scale(1)',
              opacity: 1,
              filter: 'saturate(1)',
            },
          },
        },
      }}
    >
      {content}
    </Dialog>
  )
}

export default UpgradePromptModal
